import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

// GoalCard — displays a row from public.behavior_goals.
//
// Field mapping (see supabase/migrations/0002_pbis_data_model.sql):
//   goalArea     (goal_area)       — academic | behavioral | communication | ...
//   specific     (specific)        — what will change
//   target       (target)          — target level of performance
//   progressPercent (progress_percent) — 0..100 (optional)
//   targetDate   (target_date)     — SMART "time-bound" (optional)
//   status       (status)          — not_started | in_progress | mastered | discontinued
//   lastUpdated                    — server-side touch timestamp

const DOMAIN_META = {
  academic:          { icon: 'BookOpen',       label: 'Academic' },
  behavioral:        { icon: 'Users',          label: 'Behavioral' },
  'social-emotional':{ icon: 'Heart',          label: 'Social / Emotional' },
  communication:     { icon: 'MessageCircle',  label: 'Communication' },
  functional:        { icon: 'Wrench',         label: 'Functional' },
  motor:             { icon: 'Activity',       label: 'Motor' },
  transition:        { icon: 'Compass',        label: 'Transition' },
  other:             { icon: 'Target',         label: 'Other' },
};

const STATUS_META = {
  not_started:  { label: 'Not started', className: 'bg-muted text-muted-foreground' },
  in_progress: { label: 'In progress', className: 'bg-primary/15 text-primary' },
  mastered:    { label: 'Mastered',    className: 'bg-success/15 text-success' },
  discontinued:{ label: 'Discontinued',className: 'bg-secondary text-secondary-foreground' },
};

function formatDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function progressBarColor(progress) {
  if (progress == null) return 'bg-muted-foreground/40';
  if (progress >= 80) return 'bg-success';
  if (progress >= 40) return 'bg-warning';
  return 'bg-destructive';
}

const GoalCard = ({ goal, studentName, isSelected, onSelect, onEdit, onDelete }) => {
  const domain = DOMAIN_META[goal?.goalArea] || DOMAIN_META.other;
  const status = STATUS_META[goal?.status] || STATUS_META.in_progress;
  const progress = goal?.progressPercent;
  const targetDate = formatDate(goal?.targetDate);
  const updated = formatDate(goal?.lastUpdated || goal?.createdAt);

  return (
    <div
      className={`p-4 border rounded-lg cursor-pointer transition-all duration-200 hover:shadow-md ${
        isSelected
          ? 'border-primary bg-primary/5 shadow-sm'
          : 'border-border bg-card hover:border-primary/50'
      }`}
      onClick={() => onSelect?.(goal)}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Icon name={domain.icon} size={16} className="text-primary flex-shrink-0" />
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${status.className}`}>
            {status.label}
          </span>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {onEdit && (
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e?.stopPropagation();
                onEdit(goal);
              }}
              className="h-8 w-8"
              title="Edit goal"
            >
              <Icon name="Edit2" size={14} />
            </Button>
          )}
          {onDelete && (
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e?.stopPropagation();
                onDelete(goal);
              }}
              className="h-8 w-8 text-destructive hover:text-destructive"
              title="Delete goal"
            >
              <Icon name="Trash2" size={14} />
            </Button>
          )}
        </div>
      </div>

      {/* Goal statement (specific) */}
      <h3 className="font-medium text-card-foreground mb-2 line-clamp-3">
        {goal?.specific || '(no goal statement)'}
      </h3>

      {/* Target */}
      {goal?.target && (
        <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
          <span className="font-medium text-foreground">Target: </span>
          {goal.target}
        </p>
      )}

      {/* Progress */}
      {progress != null && (
        <div className="mb-3">
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="text-muted-foreground">Progress</span>
            <span className="font-medium text-card-foreground">{progress}%</span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all duration-300 ${progressBarColor(progress)}`}
              style={{ width: `${Math.max(0, Math.min(100, progress))}%` }}
            />
          </div>
        </div>
      )}

      {/* Meta row */}
      <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        {studentName && (
          <div className="flex items-center gap-1">
            <Icon name="User" size={12} />
            <span className="truncate max-w-[8rem]">{studentName}</span>
          </div>
        )}
        <div className="flex items-center gap-1">
          <Icon name="Tag" size={12} />
          <span className="capitalize">{domain.label}</span>
        </div>
        {targetDate && (
          <div className="flex items-center gap-1">
            <Icon name="Calendar" size={12} />
            <span>Due {targetDate}</span>
          </div>
        )}
        {updated && (
          <div className="flex items-center gap-1">
            <Icon name="Clock" size={12} />
            <span>Updated {updated}</span>
          </div>
        )}
      </div>

      {/* Log data affordance. onSelect on the card body opens the detail
          view, but a labeled action makes the primary "collect data"
          workflow discoverable. */}
      <div className="mt-3 pt-3 border-t border-border/60 flex justify-end">
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e?.stopPropagation();
            onSelect?.(goal);
          }}
          className="text-xs"
        >
          <Icon name="LineChart" size={14} className="mr-1" />
          View details / Log data
        </Button>
      </div>
    </div>
  );
};

export default GoalCard;
